import React, { useContext, useState, useEffect } from 'react';
import { useTheme, styled } from '@mui/material/styles';
import {
  Drawer,
  Toolbar,
  List,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  SvgIcon,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import MapIcon from '@mui/icons-material/Map';
import LicenseIcon from '@mui/icons-material/Key';
import PeopleIcon from '@mui/icons-material/People';
import ReportIcon from '@mui/icons-material/HistoryEdu';
import CategoryIcon from '@mui/icons-material/Category';
import StandardIcon from '@mui/icons-material/Summarize';
import HistoryIcon from '@mui/icons-material/History';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import EmergencyIcon from '@mui/icons-material/Emergency';
import GarageIcon from '@mui/icons-material/Garage';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import PlaceIcon from '@mui/icons-material/Place';
import ChurchIcon from '@mui/icons-material/Church';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BalanceIcon from '@mui/icons-material/Balance';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import AlertIcon from '@mui/icons-material/Notifications';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { ReactComponent as GuardiaLogo } from '../../assets/logo_guardia.svg';
import DataIcon from '@mui/icons-material/Storage';
import { AdminPageContext, UserContext, ThemeContext } from '../Context';
import { ReactComponent as BlueNoteLogo } from '../../assets/logo_bluenote.svg';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import FlareIcon from '@mui/icons-material/Flare';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArticleIcon from '@mui/icons-material/Article';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import LockIcon from '@mui/icons-material/Lock';
import { useAdminLanguage } from './AdminI18n';

const drawerWidth = 280;

function AdminHeader() {
  const today = new Date();
  const this_year = today.getFullYear();
  const { adminPage, setAdminPage } = useContext(AdminPageContext);
  const { user, setUser } = useContext(UserContext);
  const { theme: themeMode } = useContext(ThemeContext);
  const theme = useTheme();
  
  // Langue d'administration (contexte) + état local du switch
  const { lang, setLang, t } = useAdminLanguage();
  const [language, setLanguage] = useState('FR');

  useEffect(() => {
    setLanguage(lang);
  }, [lang]);

  const handleLogout = () => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authExpiration');
    setUser(null);
  };

  const handleLanguageChange = (event) => {
    const newLang = event.target.checked ? 'NL' : 'FR';
    setLanguage(newLang);
    setLang(newLang);
  };

  const listItemButtonSx = {
    px: 2,
    py: 0.75,
    my: 0.25,
    minHeight: 42,
    borderLeft: '3px solid transparent',
    '&.Mui-selected': {
      backgroundColor: theme.palette.action.selected,
      borderLeftColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      '& .MuiListItemIcon-root': {
        color: theme.palette.primary.main,
      },
      '& .MuiListItemText-primary': {
        fontWeight: 600,
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  };

  const hiddenMenuPages = new Set(['BarcodeAdmin']);
  const hiddenEntityPages = new Set([
    'RecipientAdmin',
    'PhenomenaAdmin',
    'SubjectAdmin',
    'RegisterAdmin',
    'TypeAdmin'
  ]);

  const menuItems = [
    { text: t('Accueil'), icon: <HomeIcon />, page: '' },
    { text: t('Licence'), icon: <LicenseIcon />, page: 'LicenseAdmin' },
    { text: t('GuardIA'), icon: <GuardiaLogo style={{ width: '24px', height: '24px', color: 'inherit' }} />, page: 'GuardIAAdmin' },
    { text: t("Message d'alerte"), icon: <AlertIcon />, page: 'AlertAdmin' },
    { text: t('Utilisateurs'), icon: <PeopleIcon />, page: 'UserAdmin' },
    { text: t('Procès-verbaux'), icon: <ReportIcon />, page: 'ReportAdmin' },
    { text: t('Rubriques'), icon: <CategoryIcon />, page: 'RubricAdmin' },
    { text: t('Canevas de PV'), icon: <StandardIcon />, page: 'StandardAdmin' },
    { text: t('Annexes'), icon: <AttachFileIcon />, page: 'AppendixAdmin' },
    { text: t('Référence code-barre'), icon: <QrCode2Icon />, page: 'BarcodeAdmin' },
    { text: t('Documents zonaux'), icon: <DescriptionIcon />, page: 'DocsAdmin' },
    { text: t('Préférences zonales'), icon: <MapIcon />, page: 'ZonalAdmin' },
    { text: t('Restriction par défaut'), icon: <LockIcon />, page: 'RestrictionAdmin' },
  ];

  const entitiesItems = [
    { text: t('Magistrats'), icon: <BalanceIcon />, page: 'MagistratAdmin' },
    { text: t('Hopitaux'), icon: <EmergencyIcon />, page: 'HospitalAdmin' },
    { text: t('Dépanneurs'), icon: <GarageIcon />, page: 'GarageAdmin' },
    { text: t('Lieux de dépôt'), icon: <ArrowCircleDownIcon />, page: 'DropOffAdmin' },
    { text: t('Implantations'), icon: <PlaceIcon />, page: 'PlaceAdmin' },
    { text: t('Bourgmestres'), icon: <HowToVoteIcon />, page: 'MayorAdmin' },
    { text: t('Médecins légistes'), icon: <LocalHospitalIcon />, page: 'ExaminerAdmin' },
    { text: t('Pompes funèbres'), icon: <ChurchIcon />, page: 'FuneralAdmin' },
    { text: t('Prisons'), icon: <CalendarViewWeekIcon />, page: 'JailAdmin' },
    { text: t('Destinataires'), icon: <SendIcon />, page: 'RecipientAdmin' },
    { text: t('Phénomènes'), icon: <FlareIcon />, page: 'PhenomenaAdmin' },
    { text: t('Sujets'), icon: <PersonIcon />, page: 'SubjectAdmin' },
    { text: t('Registres'), icon: <MenuBookIcon />, page: 'RegisterAdmin' },
    { text: t('Types de PV'), icon: <ArticleIcon />, page: 'TypeAdmin' },
  ];

  const systemItems = [
    { text: t('Statistiques'), icon: <QueryStatsIcon />, page: 'StatisticsAdmin' },
    { text: t('Gestion des données'), icon: <DataIcon />, page: 'DataAdmin' },
    { text: t('Journalisation'), icon: <HistoryIcon />, page: 'LogFile' },
    { text: t('Documentation'), icon: <MenuBookIcon />, page: 'Documentation' },
    { text: t('Accès administration'), icon: <AdminPanelSettingsIcon />, page: 'AdminAccess' },
  ];

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <Toolbar sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        py: 2,
        px: 2,
        gap: 1,
        boxSizing: 'border-box',
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}>
          <BlueNoteLogo
            style={{
              height: '40px',
              width: 'auto',
              color: theme.palette.primary.main
            }}
          />
          <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
            {t('BlueNote')}
          </Typography>
        </Box>
        <Typography 
            component="span" 
            sx={{ 
              width: '100%',
              textAlign: 'center',
              color: 'text.secondary', 
              fontStyle: 'italic', 
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis' 
            }}
          >
            <SettingsFieldDisplay fieldName="clientName" />
          </Typography>
        
        {/* Switch de langue FR/NL */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          width: '100%', 
          justifyContent: 'center',
          mt: 1 
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: language === 'FR' ? 600 : 400,
              color: language === 'FR' ? 'primary.main' : 'text.secondary'
            }}
          >
            {t('FR')}
          </Typography>
          <Switch
            checked={language === 'NL'}
            onChange={handleLanguageChange}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: theme.palette.primary.main,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: theme.palette.primary.main,
              },
            }}
          />
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: language === 'NL' ? 600 : 400,
              color: language === 'NL' ? 'primary.main' : 'text.secondary'
            }}
          >
            {t('NL')}
          </Typography>
        </Box>
        
      </Toolbar>
      <Divider />
      <Box sx={{ overflow: 'auto', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <List sx={{ pt: 0 }}>
          {menuItems
            .filter((item) => !hiddenMenuPages.has(item.page))
            .map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => setAdminPage(item.page)}
                selected={adminPage === item.page}
                sx={listItemButtonSx}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'action.active' }}>{item.icon}</ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.9rem' }} primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
          
          <Accordion 
            defaultExpanded={false}
            sx={{ 
              boxShadow: 'none',
              mt: 1,
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                minHeight: 40,
                px: 2,
                '&.Mui-expanded': {
                  minHeight: 40,
                },
              },
              '& .MuiAccordionDetails-root': {
                px: 0,
                pb: 1,
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="entities-content"
              id="entities-header"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                <BusinessIcon fontSize="small" sx={{ mr: 2, color: 'action.active' }} />
                <Typography sx={{ fontWeight: 400, fontSize: '0.95rem' }}>{t('Données zonales')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <List sx={{ pt: 0 }}>
                {entitiesItems
                  .filter((item) => !hiddenEntityPages.has(item.page))
                  .map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      onClick={() => setAdminPage(item.page)}
                      selected={adminPage === item.page}
                      sx={{ ...listItemButtonSx, pl: 4 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: 'action.active' }}>{item.icon}</ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: '0.9rem' }} primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>

          {systemItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => setAdminPage(item.page)}
                selected={adminPage === item.page}
                sx={listItemButtonSx}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'action.active' }}>{item.icon}</ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.9rem' }} primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={handleLogout}
              sx={{
                my: 0.5,
                mx: 1,
                borderRadius: 1,
                color: theme.palette.error.main,
                '& .MuiListItemIcon-root': { color: theme.palette.error.main },
                '&:hover': {
                  backgroundColor: theme.palette.error.light,
                  color: theme.palette.error.contrastText,
                  '& .MuiListItemIcon-root': { color: theme.palette.error.contrastText },
                },
              }}
            >
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontWeight: 600 }} primary={t('Déconnexion')} />
            </ListItemButton>
          </ListItem>
        </List>
        <Box sx={{ flexGrow: 1 }} />
      </Box>
      <Divider />
      <Typography
        variant="caption"
        align="center"
        sx={{ p: 1, color: 'text.secondary' }}
      >
        v<SettingsFieldDisplay fieldName="version" /> ©{this_year} by{' '}
        <a 
          href="https://pyll.be/bluenote" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ 
            background: themeMode === 'dark' 
              ? 'linear-gradient(135deg,rgb(68, 167, 233),rgb(83, 139, 196))'
              : 'linear-gradient(135deg, #667eea, #1a253c)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            transition: 'all 0.2s ease-in-out',
            display: 'inline-block',
            lineHeight: '1.6',
            padding: '2px 0',
            backgroundSize: '100% 100%'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
          }}
        >
          <i style={{ fontSize: '1.2em', lineHeight: 'inherit' }}>pyll</i> <b style={{ fontSize: '0.9em', lineHeight: 'inherit' }}>SRL</b>
        </a>
      </Typography>
    </Drawer>
  );
}

export default AdminHeader;
